import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import iconManagement from "../../assets/icon-management.svg";
import iconPublishing from "../../assets/icon-publishing.svg";
import QuoteModal from "./quote-modal";

const TABS = [
    // {
    //     id: "management",
    //     icon: iconManagement,
    //     title: "Project Management",
    //     description: "Manage and track construction projects with clarity and keep your team connected with better collaboration.",
    // },
    {
        id: "publishing",
        icon: iconPublishing,
        title: "Project Publishing",
        description: "Publish and showcase your products with the global audience.",
    },
];

const managementPlans = [
    {
        name: "Basic",
        monthlyPrice: 50,
        badge: "Save up to 25%",
        buttonLabel: "Start Free Trial - Basic",
        featured: false,
        tagline: "Perfect for individuals, small teams, interior designers, renovators & landscaping",
        intro: null,
        features: [
            "Manage up to 2 workspaces",
            "Team collaboration up to 5 users",
            "2GB files & photo storage",
            "Up to 5 meeting scheduling",
            "Access to basic AI",
            "Add a new user at $5 per month",
            "Project codes allocated to the project",
        ],
    },
    {
        name: "Standard",
        monthlyPrice: 100,
        badge: "Save up to 25%",
        buttonLabel: "Start Free Trial - Pro",
        featured: true,
        tagline: "Built for commercial sales, rentals, and high volume leads",
        intro: "All in Basic:",
        features: [
            "Manage up to 7+ workspaces",
            "Team collaboration up to 15 users",
            "Subcontractors & consultants may be within the projects",
            "Unlimited scheduling & meetings",
            "Add a new user at $8 per month",
            "Unlimited files & photo storage",
            "Project codes allocated to the project",
            "Access to basic AI",
        ],
    },
    {
        name: "Enterprise",
        monthlyPrice: null,
        badge: null,
        buttonLabel: "Request a Quote",
        featured: false,
        tagline: "Built for large scale developers, government contractors & multi-site commercial portfolios.",
        intro: "All in Basic, Standard:",
        features: [
            "Dedicated account manager",
            "24/7 support",
            "Compliance tracking and inspection management",
            "API access and integration",
        ],
    },
];

const publishingPlans = [
    {
        name: "Free",
        monthlyPrice: 0,
        badge: null,
        subtitle: "Build your verified presence and get discovered.",
        buttonLabel: "Create your profile",
        featured: false,
        tagline: "Free forever",
        intro: null,
        features: [
            "Global Unique ConRa Code: Get a permanent identifier for your profile and projects to share with clients anywhere.",
            "Past portfolio archive: Upload completed builds to showcase a verified track record and prove past experience.",
            "C2PA tamper-proof media: Protect site photos with cryptographic signatures that prove authenticity and prevent fraud.",
            "Global directory listing: Get found by prospective developers, lenders, and clients searching for verified builders.",
            "Multi-device access: View and manage your project history seamlessly across mobile, web, and desktop.",
        ],
    },
    {
        name: "Standard",
        monthlyPrice: 39,
        badge: "Most common",
        subtitle: "Verify a live build, remotely trusted.",
        buttonLabel: "Start free trial",
        featured: false,
        tagline: "For individuals, diaspora investors, and small teams—including local and international clients—managing remote builds with total transparency.",
        intro: "Includes everything in Free",
        features: [
            "One-Click Team Sharing: Share live project dashboards with clients, family, or consultants via a dedicated link.",
            "Cryptographically locked, location-verified progress updates logged directly from the property.",
            "Instant WhatsApp & Email Alerts: Stakeholders receive live, automated site milestone notifications right where they check messages daily.",
            "Generous Bookmark Limits: Share updates with multiple remote followers without incurring per-user charges.",
            "Live Progress Feeds: Keep international and local clients updated continuously without manual site reports or constant phone calls.",
        ],
    },
    {
        name: "Enterprise",
        monthlyPrice: null,
        badge: null,
        buttonLabel: "Request a Quote",
        featured: false,
        tagline: "For real estate developers, financial institutions, NGOs, and governments",
        intro: "All in Free, Basic:",
        features: [
            "Direct API connections & webhook streams",
            "Integrates into your existing CRM or property system as-is",
            "Unlimited bookmarks & milestone broadcasting at scale",
            "Multi-tier portfolio monitoring & analytics",
            "Secure project admin controls & role permissions",
            "Dedicated solution architecture support",
            "10-year proof retention",
        ],
    },
];

const PricingPlan = ({ activeTab, setActiveTab }) => {
    const [isYearly, setIsYearly] = useState(false);
    const [isQuoteOpen, setIsQuoteOpen] = useState(false);

    const plans = activeTab === "management" ? managementPlans : publishingPlans;

    const getPrice = (plan) => {
        if (plan.monthlyPrice === null) return null;
        if (plan.monthlyPrice === 0) return "0";
        return isYearly
            ? (plan.monthlyPrice * 12 * 0.75).toFixed(0)
            : plan.monthlyPrice.toFixed(0);
    };

    return (
        <section id="pricing">
            <div className="pricing-container">
                <h1 className="h1-48-600">
                    Simple and Transparent Pricing
                </h1>
                <p className="pricing-italic-sub">Built for your Projects.</p>
                <p className="pricing-tagline-sub">Choose a perfect plan for the way you work.</p>

                {/* Product type selector — hidden: the plan set is now
                    determined by the visitor's product flow (see utils/product-flow) */}
                {false && (
                    <>
                        <div className="ptype-selector">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`ptype-card ${activeTab === tab.id ? "ptype-card--active" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <img src={tab.icon} alt={tab.title} className="ptype-icon" />
                                    <div className="ptype-text">
                                        <span className="ptype-title">{tab.title}</span>
                                        <span className="ptype-desc">{tab.description}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <hr className="ptype-divider" />
                    </>
                )}

                {/* Billing toggle */}
                <div className="toggle-container" style={{ marginTop: "24px" }}>
                    <h4>Monthly</h4>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={isYearly}
                            onChange={() => setIsYearly(!isYearly)}
                        />
                        <span className="slider"></span>
                    </label>
                    <h4><span>Yearly (save 25%)</span></h4>
                </div>

                {/* Currency */}
                <div className="currency-selector">
                    <label>Currency</label>
                    <select>
                        <option>US Dollar</option>
                    </select>
                </div>

                {/* Pricing cards */}
                <div className="pplans-grid">
                    {plans.map((plan) => {
                        const price = getPrice(plan);
                        return (
                            <div
                                key={plan.name}
                                className={`pplan-card ${plan.featured ? "pplan-card--featured" : ""}`}
                            >
                                {plan.badge && (
                                    <span className="pplan-badge">{plan.badge}</span>
                                )}

                                <h2 className="pplan-name">{plan.name}</h2>

                                <p className="pplan-tagline">{plan.tagline}</p>

                                {/* {plan.subtitle && (
                                    <p className="pplan-subtitle">{plan.subtitle}</p>
                                )} */}

                                <div className="pplan-price">
                                    {price === null ? (
                                        <>
                                            <span className="pplan-amount">Custom</span>
                                            <span className="pplan-period">annual billing only</span>
                                        </>
                                    ) : price === "0" ? (
                                        <>
                                            <span className="pplan-amount">Free</span>
                                            <span className="pplan-period">forever</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="pplan-amount">${price}</span>
                                            <span className="pplan-period">
                                                per project / {isYearly ? "year" : "month"}
                                            </span>
                                        </>
                                    )}
                                </div>

                                <button
                                    className="pplan-btn"
                                    onClick={() =>
                                        plan.buttonLabel === "Request a Quote"
                                            ? setIsQuoteOpen(true)
                                            : window.open("https://web.conra.net", "_blank")
                                    }
                                >
                                    {plan.buttonLabel}
                                </button>

                                <hr className="pplan-divider" />

                                <div className="pplan-features">
                                    {plan.intro && (
                                        <p className="pplan-intro">{plan.intro}</p>
                                    )}
                                    <ul>
                                        {plan.features.map((f, i) => (
                                            <li key={i}>
                                                <FaCheckCircle className="pplan-check" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <hr className={`pplan-feature-divider ${plan.featured ? "pplan-feature-divider--featured" : ""}`} />

                                <a href="#price-compare" className="pplan-compare-link">
                                    View comparison →
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>

            <QuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />
        </section>
    );
};

export default PricingPlan;
